import type { CompanyFilters } from "../../types";
import { normalizeStringArray } from "../../utils/normalize";
import { match } from "ts-pattern";
import { defaultIntentSlice, type IntentSlice } from "./slice";

// Extract GraphQL types from IntentFilter to stay decoupled from codegen
type IntentFilterInput = NonNullable<CompanyFilters["intent"]>;
type GraphQLInclusionMethod = NonNullable<
  IntentFilterInput["topics"]
>["inclusionMethod"];
type GraphQLSignalScore = IntentFilterInput["signalScore"];

/**
 * Converts slice state to CompanyFilters for API queries.
 * Only produces filter when topics are selected.
 */
export function toCompanyFilters(slice: IntentSlice): Partial<CompanyFilters> {
  // Intent filter requires at least one topic to be meaningful
  if (slice.topicIds.length === 0) {
    return {};
  }

  return {
    intent: {
      ...(slice.collectionId ? { collectionId: slice.collectionId } : {}),
      topics: {
        ids: slice.topicIds,
        inclusionMethod: slice.topicsInclusionMethod as GraphQLInclusionMethod,
      },
      buyersJourneyIds:
        slice.buyersJourneyIds.length > 0 ? slice.buyersJourneyIds : null,
      signalScore: slice.signalScore as GraphQLSignalScore,
      contextTypeIds:
        slice.contextTypeIds.length > 0 ? slice.contextTypeIds : null,
      location: {
        countryAlpha2s:
          slice.location.countryAlpha2s.length > 0
            ? slice.location.countryAlpha2s
            : null,
        regionNames:
          slice.location.regionNames.length > 0
            ? slice.location.regionNames
            : null,
        stateIds:
          slice.location.stateIds.length > 0 ? slice.location.stateIds : null,
        excludedCountryAlpha2s:
          slice.location.excludedCountryAlpha2s.length > 0
            ? slice.location.excludedCountryAlpha2s
            : null,
      },
      numberOfCadences: slice.numberOfCadences,
    },
  };
}

/**
 * Hydrates slice state from saved CompanyFilters.
 * Note: topicsCache must be populated separately via API calls.
 */
export function fromCompanyFilters(filters: CompanyFilters): IntentSlice {
  if (!filters.intent?.topics?.ids?.length) {
    return defaultIntentSlice;
  }

  const { inclusionMethod } = filters.intent.topics;
  // Only accept valid IntentSlice inclusion methods, default to ANY_PRESENT
  const topicsInclusionMethod: IntentSlice["topicsInclusionMethod"] =
    inclusionMethod === "ALL_PRESENT" || inclusionMethod === "ANY_PRESENT"
      ? inclusionMethod
      : "ANY_PRESENT";

  const { signalScore } = filters.intent;
  // Only accept valid IntentSlice signal scores, default to HIGH
  const normalizedSignalScore: IntentSlice["signalScore"] =
    signalScore === "HIGH" || signalScore === "MEDIUM" ? signalScore : "HIGH";

  const location = {
    countryAlpha2s: normalizeStringArray(
      filters.intent.location?.countryAlpha2s
    ),
    excludedCountryAlpha2s: normalizeStringArray(
      filters.intent.location?.excludedCountryAlpha2s
    ),
    regionNames: normalizeStringArray(filters.intent.location?.regionNames),
    stateIds: normalizeStringArray(filters.intent.location?.stateIds),
  };

  const locationApproach = match(location)
    .returnType<IntentSlice["location"]["approach"]>()
    .when(
      (l) => l.stateIds.length > 0,
      () => "states"
    )
    .when(
      (l) => l.regionNames.length > 0,
      () => "regions"
    )
    .when(
      (l) => l.countryAlpha2s.length > 0 || l.excludedCountryAlpha2s.length > 0,
      () => "countries"
    )
    .otherwise(() => "regions");

  return {
    topicIds: normalizeStringArray(filters.intent.topics.ids),
    topicsInclusionMethod,
    collectionId: filters.intent.collectionId ?? null,
    collectionName: null,
    signalScore: normalizedSignalScore,
    buyersJourneyIds: normalizeStringArray(filters.intent.buyersJourneyIds),
    contextTypeIds: normalizeStringArray(filters.intent.contextTypeIds),
    numberOfCadences: filters.intent.numberOfCadences ?? null,
    location: {
      approach: locationApproach,
      ...location,
    },
    // Cache empty - will be populated by hydration query
    topicsCache: [],
  };
}
